import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { StyleSheet, Text } from 'react-native';
import {
  BodyText,
  getFormattedExpirationDate,
  ModalDialogue,
  ModalRow,
  TextLink,
  TextLinkify,
  theme,
} from '@sg/garnish';

import type { ChallengeRewardsQuery } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { useRewardDetailsModalTelemetry } from './RewardDetailsModal.telemetry';

export const RewardDetailsModal = (props: ChallengesScreenRewardModalProps) => {
  const { reward, onDismiss } = props;
  const { t } = useLocalizationContext();
  const {
    handleRewardDetailsTelemetry,
    handleRewardClosedTelemetry,
    handleRewardLinkTelemetry,
  } = useRewardDetailsModalTelemetry();

  const [isVisible, setIsVisible] = useState(Boolean(reward));

  const rewardName = reward?.name ?? '';
  const terms = reward?.terms ?? t('challenges.rewards.modal.terms');
  const rewardExpirationDate =
    getFormattedExpirationDate(reward?.expiresAt) ?? '';

  // ─── HELPERS ─────────────────────────────────────────────────────

  const hideModal = useCallback(() => {
    setIsVisible(false);
    handleRewardClosedTelemetry();
  }, [handleRewardClosedTelemetry]);

  // ─── EFFECTS ─────────────────────────────────────────────────────

  useEffect(() => {
    setIsVisible(Boolean(reward));

    if (reward) handleRewardDetailsTelemetry(reward.id);
  }, [reward, handleRewardDetailsTelemetry]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      onRequestClose={hideModal}
      onDismissed={onDismiss}
      visible={isVisible}
      size="small"
      testID="challenges.rewards.modal"
      headline={rewardName}
      subhead={rewardExpirationDate}
      withBottomPadding
    >
      <ModalRow>
        <BodyText size={3} style={styles.rewardModalText}>
          {t('challenges.rewards.modal.text')}
        </BodyText>

        <BodyText size={3} style={styles.rewardModalText}>
          <TextLinkify>{terms}</TextLinkify>
        </BodyText>

        <BodyText size={3}>
          {t('general.see')}
          <Text> </Text>
          <TextLink
            href={REWARDS_TERMS_LINK}
            onPress={handleRewardLinkTelemetry}
          >
            {REWARDS_TERMS_LINK}
            <Text>.</Text>
          </TextLink>
        </BodyText>
      </ModalRow>
    </ModalDialogue>
  );
};

const styles = StyleSheet.create({
  rewardModalText: {
    paddingBottom: theme.spacing['4'],
  },
});

// ─── CONSTANTS ──────────────────────────────────────────────────────────────────

const REWARDS_TERMS_LINK = 'https://www.sweetgreen.com/sweet-rewards';

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type ChallengesScreenRewardModalProps = {
  readonly reward: ChallengeRewardsQuery['challengeRewards'][0] | undefined;
  readonly onDismiss: () => void;
};
